import { useEffect, useRef } from 'react';
import { Toolbar } from './toolbar/Toolbar';
import { Canvas } from './canvas/Canvas';
import { DetailPanel } from './panel/DetailPanel';
import { PanelShell, ResizeHandle } from './panel/ResizeHandle';
import { VariablesModal } from './modals/VariablesModal';
import { ObjectivesModal } from './modals/ObjectivesModal';
import { ProblemsModal } from './modals/ProblemsModal';
import { VersionsModal } from './modals/VersionsModal';
import { LibraryModal } from './modals/LibraryModal';
import { SettingsModal } from './modals/SettingsModal';
import { useScenarioStore } from './store/scenarioStore';

/**
 * Branchworks editor shell. Phase 5a adds persistence: the app hydrates from
 * the repository on mount, then autosaves (debounced) whenever the scenario or
 * its published versions change.
 */
export default function App() {
  const openModal = useScenarioStore((s) => s.openModal);
  const hydrate = useScenarioStore((s) => s.hydrate);
  const hydrated = useScenarioStore((s) => s.hydrated);
  const scenario = useScenarioStore((s) => s.scenario);
  const publishedVersions = useScenarioStore((s) => s.publishedVersions);
  const saveNow = useScenarioStore((s) => s.saveNow);
  const setSaveStatus = useScenarioStore((s) => s.setSaveStatus);

  // Hydrate once on mount.
  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  // Debounced autosave. Whenever the scenario or its published versions change
  // (and we're hydrated), schedule a save ~600ms later. A new change resets the
  // timer, so rapid edits coalesce into one write. The `saving` status flips
  // immediately so the toolbar reflects pending work; `saved`/`error` lands
  // when the write resolves.
  const saveTimer = useRef<number | null>(null);
  const firstAutosaveSkip = useRef(false);
  useEffect(() => {
    if (!hydrated) return;
    // Skip the very first run after hydration — hydration already loaded a
    // saved record; re-saving it immediately would be a redundant write.
    if (!firstAutosaveSkip.current) {
      firstAutosaveSkip.current = true;
      return;
    }
    setSaveStatus('saving');
    if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      void saveNow();
    }, 600);
    return () => {
      if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
    };
  }, [scenario, publishedVersions, hydrated, saveNow, setSaveStatus]);

  return (
    <div className="w-full h-screen flex flex-col">
      <Toolbar />
      <main className="flex-1 min-h-0 flex relative">
        <div className="flex-1 min-w-0">
          <Canvas />
        </div>
        <ResizeHandle />
        <PanelShell>
          <DetailPanel />
        </PanelShell>
      </main>
      {openModal === 'variables' && <VariablesModal />}
      {openModal === 'objectives' && <ObjectivesModal />}
      {openModal === 'problems' && <ProblemsModal />}
      {openModal === 'versions' && <VersionsModal />}
      {openModal === 'library' && <LibraryModal />}
      {openModal === 'settings' && <SettingsModal />}
    </div>
  );
}
